import { Project } from '@griply/assets';
import { EntityLinks, Goal, GoalType, ID, LifeArea } from '@griply/types';
import {
  getDirectGoalLifeAreaIds,
  getLinkedItemColor,
  GoalParentKind,
  isGoalArchived,
  resolveGoalParents,
} from '@griply/utils';
import { t } from 'i18next';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronLeft,
  IconFormIcons,
  Lock,
  Target,
} from 'shared/assets/icons';
import { GoalContextMenu } from 'shared/components/connected/goal-context-menu';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { useDragPreview } from 'shared/components/ui/virtualized-tree-list';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { Paths } from 'shared/routes/paths';
import { getGoalDetailUrl } from 'shared/utils/get-goal-detail-url';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { ContextMenuOptions } from 'shared/views/goal-filter-list/types';
import { useTheme } from 'styled-components';

import { CtaContainer } from './cta-container';
import { DeadlineCell } from './deadline-cell';
import * as Styled from './goal-list-card.style';
import { ImpactCell } from './impact-cell';
import { MetricCell } from './metric-cell';
import { StartDateCell } from './start-date-cell';
import { TasksCell } from './tasks-cell';

export type GoalListCardProps = {
  goal: Goal;
  onClick?: (id: ID) => void;
  onUpdateProgress?: (id: ID) => void;

  lifeAreas: LifeArea[];
  goals: Goal[];

  hideLifeArea?: boolean;
  showParentGoalLabel?: boolean;
  showParentLifeAreaLabel?: boolean;
  // Projects always get the overline when a kind is on; this extends it to plain goals.
  showParentLabelsForGoals?: boolean;
  links?: EntityLinks;
  /** The link this row sits under — what "Move to" replaces. */
  sourceLink?: { goalId?: ID; lifeAreaId?: ID };
  isExpanded?: boolean;
  onToggleExpand?: (e: React.MouseEvent) => void;
  hasChildren?: boolean;
  isSelected?: boolean;
  isDropTarget?: boolean;
  preventInteraction?: boolean;
  hideContextMenu?: boolean;
  excludedContextMenuOptions?: ContextMenuOptions[];
  indentLevel?: number;
};

export const GoalListCard: React.FC<GoalListCardProps> = ({
  goal,
  onClick,
  onUpdateProgress,
  goals,
  lifeAreas,
  hideLifeArea,
  showParentGoalLabel,
  showParentLifeAreaLabel,
  showParentLabelsForGoals,
  links,
  sourceLink,
  isExpanded,
  onToggleExpand,
  hasChildren,
  isSelected,
  isDropTarget,
  preventInteraction,
  hideContextMenu,
  excludedContextMenuOptions,
  indentLevel,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDragPreview = useDragPreview();
  const effectiveIndentLevel = isDragPreview ? 0 : indentLevel ?? 0;

  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState({
    disabled: !!goal.frozenAt || hideContextMenu,
  });

  const showSelection = isSelected || !!menuPosition;

  const color = useMemo(
    () => getLinkedItemColor(goal.id, { goals, lifeAreas }),
    [goal.id, goals, lifeAreas],
  );

  const isArchived = useMemo(
    () => isGoalArchived(goal, goals, { links }),
    [goal, goals, links],
  );

  const taskCounts = useTaskCounts();

  const isProject = goal.type === GoalType.Project;

  // Clickable overline listing the row's parents; a kind is hidden when the list is already grouped by it.
  const parents = useMemo(() => {
    if (!isProject && !showParentLabelsForGoals) {
      return [];
    }
    return resolveGoalParents(goal, { goals, lifeAreas, links }).flatMap(
      (resolved) => {
        if (resolved.kind === GoalParentKind.Goal) {
          return showParentGoalLabel
            ? [{ id: resolved.goal.id, name: resolved.goal.name, isGoal: true }]
            : [];
        }
        return showParentLifeAreaLabel
          ? [
              {
                id: resolved.lifeArea.id,
                name: resolved.lifeArea.name,
                isGoal: false,
              },
            ]
          : [];
      },
    );
  }, [
    goal,
    goals,
    lifeAreas,
    links,
    isProject,
    showParentLabelsForGoals,
    showParentGoalLabel,
    showParentLifeAreaLabel,
  ]);

  const onParentClick = (
    e: React.MouseEvent,
    parent: { id: ID; isGoal: boolean },
  ) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(
      replaceUrlParams(
        parent.isGoal ? Paths.GoalDetail : Paths.LifeAreaDetail,
        {
          id: parent.id,
        },
      ),
    );
  };

  const lifeArea = useMemo(() => {
    const [directId] = getDirectGoalLifeAreaIds(goal, links);
    return lifeAreas.find(({ id }) => id === directId);
  }, [goal, lifeAreas, links]);
  return (
    <>
      <CtaContainer
        id={goal.id}
        title={goal.name}
        detailUrl={getGoalDetailUrl(goal)}
        isFrozen={!!goal.frozenAt}
        onClick={onClick}
        preventInteraction={preventInteraction}
      >
        <Styled.Container
          onContextMenu={openMenu}
          $isArchived={isArchived}
          $isSelected={showSelection}
          $isDropTarget={isDropTarget}
        >
          {/* image and title */}
          <Styled.DataContainer
            $type={Styled.DataTypes.Title}
            $isFrozen={!!goal.frozenAt}
          >
            <Styled.IllustrationContainer $indentLevel={effectiveIndentLevel}>
              <Styled.ImageContainer
                $iconColor={isProject ? color ?? undefined : undefined}
                $transparent={isProject}
              >
                {isProject ? (
                  <Icon icon={Project} size={16} />
                ) : goal.image ? (
                  <Styled.Image
                    src={goal.image.url}
                    alt={goal.name}
                    hash={goal.image.blurHash}
                  />
                ) : (
                  <Icon icon={IconFormIcons[goal.iconName!] ?? Target} />
                )}
              </Styled.ImageContainer>

              {!!goal.completedAt && (
                <Styled.CompletedContainer>
                  <Icon icon={Check} />
                </Styled.CompletedContainer>
              )}

              {!!color && !isProject && <Styled.ColorDot $color={color} />}
            </Styled.IllustrationContainer>

            <Styled.TitleColumn>
              <Styled.Title $isCompleted={!!goal.completedAt}>
                {goal.name}
              </Styled.Title>
              {!!parents.length && (
                <Styled.ParentLabels>
                  {parents.map((parent, index) => {
                    const handleParentClick = (e: React.MouseEvent) =>
                      onParentClick(e, parent);
                    return (
                      <React.Fragment key={`${parent.isGoal}-${parent.id}`}>
                        {index > 0 && (
                          <Styled.ParentSeparator>, </Styled.ParentSeparator>
                        )}
                        <Styled.ParentGoalLabel
                          type="button"
                          onClick={handleParentClick}
                        >
                          {parent.name}
                        </Styled.ParentGoalLabel>
                      </React.Fragment>
                    );
                  })}
                </Styled.ParentLabels>
              )}
            </Styled.TitleColumn>

            {goal.frozenAt && (
              <Styled.IconContainer>
                <Icon icon={Lock} />
              </Styled.IconContainer>
            )}
          </Styled.DataContainer>

          {!isDragPreview && (
            <>
              <Styled.RightGroup>
                <MetricCell
                  goal={goal}
                  goals={goals}
                  links={links}
                  isProject={isProject}
                  goalProgressCounts={taskCounts?.byGoalProgress}
                  onUpdateProgress={onUpdateProgress}
                  onCloseContextMenu={closeMenu}
                />

                <ImpactCell goal={goal} />

                <StartDateCell goal={goal} />

                <DeadlineCell goal={goal} />

                <TasksCell goal={goal} />

                {/* life area */}
                {!hideLifeArea && (
                  <Styled.DataContainer
                    $type={Styled.DataTypes.LifeArea}
                    $isFrozen={!!goal.frozenAt}
                  >
                    {lifeArea?.name ? (
                      <Styled.OverflowSpan>{lifeArea.name}</Styled.OverflowSpan>
                    ) : (
                      <Styled.SelectPlaceholder>
                        {t('general.labels.select')}
                      </Styled.SelectPlaceholder>
                    )}
                  </Styled.DataContainer>
                )}

                {/* context menu */}
                <Styled.DataContainer
                  $type={Styled.DataTypes.Interactions}
                  $isFrozen={!!goal.frozenAt}
                >
                  {hasChildren && (
                    <IconButton
                      icon={ChevronLeft}
                      onClick={onToggleExpand}
                      rotation={isExpanded ? -90 : 0}
                      color={Colors.Default}
                    />
                  )}
                </Styled.DataContainer>
              </Styled.RightGroup>
            </>
          )}
        </Styled.Container>
      </CtaContainer>

      {!!menuPosition && !theme.isMobile && (
        <GoalContextMenu
          id={goal.id}
          location={menuPosition}
          referenceElement={undefined}
          onClose={closeMenu}
          excludedOptions={excludedContextMenuOptions}
          sourceLink={sourceLink}
        />
      )}
    </>
  );
};
